import { Injectable, effect, inject } from '@angular/core';
import { UUID } from '@finxone-platform/shared/base-types';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  PageTemplateConfig,
  Stack,
  WorkflowConfig,
  WorkflowZoneDevicesConfig,
  ZoneConfiguration,
  defaultWorkflowConfig,
  templateEditorWorkflowConfig,
  workflowConfigLatestVersion,
} from '@finxone-platform/shared/sys-config-types';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  addDefaultNestedWidgetsIfNeeded,
  getFirstMigrationVersion,
  getMigrationFunctions,
  mapWorkflowToSystem,
  rebuildProjectForms,
} from '@finxone-platform/shared/workflow-mapping';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import * as Sentry from '@sentry/angular';
import { saveAs } from 'file-saver';
import * as jsondiffpatch from 'jsondiffpatch';
import JSZip from 'jszip';
import { cloneDeep } from 'lodash';
import LZString from 'lz-string';
import * as objectMapper from 'object-mapper';
import { BehaviorSubject, Observable, fromEvent, take, tap } from 'rxjs';
import {
  AddProgressBarWorkflowStack,
  RemoveProgressBarWorkflowStack,
} from '../../actions/progress-bar-workflow.action';
import { ProjectsState } from '../../state/project.state';
import { ActiveConfigService } from '../active-config/active-config.service';
import { PageTemplateService } from '../page-template-service/page-template.service';
import { RouteService } from '../route-service/route.service';
import {
  SocketService,
  WorkflowConfigChangeStatus,
  WorkflowConfigDeltaAction,
  WorkflowConfigFailedToApplyDiffAction,
  WorkflowConfigFullUpdateAction,
} from '../socket-service/socket-service.service';
import { SystemConfigService } from '../system-config/system-config.service';
import { worker } from './get-worker.utils';
import { PageNavigationMapService } from '../page-navigation-map/page-navigation-map.service';

@Injectable({
  providedIn: 'root',
})
export class WorkflowConfigService {
  private worker: Worker;
  private workflowConfig$: BehaviorSubject<WorkflowConfig> = new BehaviorSubject(defaultWorkflowConfig);
  // Used to help generate real diffs as sometimes fields are change in place before modification
  private finalWorkflowConfig: WorkflowConfig;

  // 100 was plucked out of thin air, can be less
  public undoHistoryStack = new Stack<jsondiffpatch.Delta>(100);
  public redoHistoryStack = new Stack<jsondiffpatch.Delta>(100);

  public receivedHistoryStack = new Stack<{
    diff: jsondiffpatch.Delta;
    time: Date;
    stack: string | undefined;
  }>(200);
  public sentHistoryStack = new Stack<{
    diff: jsondiffpatch.Delta;
    time: Date;
    stack: string | undefined;
    id: string;
    status?: string;
  }>(200);

  jsondiffpatchInstance = jsondiffpatch.create({
    // see for why this is needed: https://github.com/benjamine/jsondiffpatch/blob/master/docs/arrays.md
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    objectHash: (obj: any, index: any) => this.objectHash(obj, index),
  });

  private objectHash(obj: any, index: any) {
    // try to find an id property, otherwise just use the index in the array
    if (!obj || typeof obj !== 'object') {
      return '$$index:' + index;
    }
    return (
      obj.version ||
      obj.pageUniqueId ||
      obj.uniqueId ||
      obj.id ||
      obj._id ||
      obj.slug ||
      obj.name ||
      '$$index:' + index
    );
  }

  private lastChangeSentId = '';

  private workflowConfigFullUpdateLoadingSubject = new BehaviorSubject<boolean>(false);
  public workflowConfigFullUpdateLoading$ = this.workflowConfigFullUpdateLoadingSubject.asObservable();

  // Add a queue for batching updates
  private updateQueue: Array<{
    fn: (config: WorkflowConfig) => WorkflowConfig;
    isGridPositionUpdate: boolean;
    isRedoOperation: boolean;
    undoOrRedoOperation: boolean;
    shouldDistributeChange: boolean;
    stackTrace?: string;
  }> = [];
  private queueTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly BATCH_DELAY = 150; // 150ms batch window

  public workflowConfigFullUpdateLoading() {
    this.workflowConfigFullUpdateLoadingSubject.next(true);
  }

  public workflowConfigFullUpdateLoaded() {
    this.workflowConfigFullUpdateLoadingSubject.next(false);
  }

  private recentUpdateConfigCalls: { hash: string; timestamp: number }[] = [];
  private LOOP_DETECTION_WINDOW = 1000; // ms
  private MAX_DUPLICATE_CALLS = 3;

  private readonly pageNavigationMapService = inject(PageNavigationMapService);

  constructor(
    private systemConfigService: SystemConfigService,
    private store: Store,
    private actions: Actions,
    private socketService: SocketService,
    private alertHandlerService: AlertHandlerService,
    private routeService: RouteService,
    private activeConfigService: ActiveConfigService,
    private pageTemplateService: PageTemplateService,
  ) {
    this.workflowConfigFullUpdateLoading();
    let config = this.getWorkFlowDataFromLocalStorage();
    this.finalWorkflowConfig = JSON.parse(JSON.stringify(config));
    config = this.upgradeWorkflowConfigVersion(config);
    this.workflowConfig$.next(config);

    // Utility to expose this service on the console
    // in the console force an upgrade using window.workflowConfigService.forceUpgradeWorkflowLocalStorage('v1.0.9')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any)['workflowConfigService'] = this;

    this.worker = worker();

    this.worker.onmessage = (compressedWorkflowData) => {
      localStorage.setItem('workflowConfig', compressedWorkflowData.data);
      console.log('Saved config using worker thread for compression');
      this.store.dispatch(new RemoveProgressBarWorkflowStack({ uniqueId: 'updateConfigDistributeChange' }));
      this.workflowConfigFullUpdateLoaded();
    };

    const source$ = fromEvent<StorageEvent>(window, 'storage');

    source$.subscribe((data) => {
      if (data.key === 'workflowConfig') {
        const config = this.getWorkFlowDataFromLocalStorage();
        this.workflowConfig$.next(config);
        this.workflowConfigFullUpdateLoaded();
        this.systemConfigService.updateConfig(() => {
          return mapWorkflowToSystem(config);
        }, false);
      }
    });

    this.actions.pipe(ofActionDispatched(WorkflowConfigDeltaAction)).subscribe((action) => {
      // this method is listening for workFlowConfig diffs on the socket service

      // log all changes recieved here
      this.receivedHistoryStack.push({
        diff: action.diff,
        stack: action.stack,
        time: action.time ?? new Date(),
      });

      // don't apply changes we sent (the socket service tracks every diff still in flight, not
      // only the last one, and hands back as foreign any a resync dropped from our state)
      if (!action.own && !this.routeService.isTemplateEditorRoute()) {
        this.alertHandlerService.showAlertFn('success', 'Received update');
        this.applyDiff(action.diff);
      }
    });

    this.actions.pipe(ofActionDispatched(WorkflowConfigFailedToApplyDiffAction)).subscribe((action) => {
      // this method is listening for failures to apply the diff on the socket service

      this.sentHistoryStack.updateItem(
        (entry) => entry.id === action.payload.id,
        (entry) => {
          entry.status = WorkflowConfigChangeStatus.FAIL;
        },
      );
    });

    this.actions.pipe(ofActionDispatched(WorkflowConfigFullUpdateAction)).subscribe((action) => {
      // this method is listening for workFlowConfig full updates on the socket service

      // don't apply changes we sent
      if (this.lastChangeSentId != action.id && !this.routeService.isTemplateEditorRoute()) {
        // Here we added following line because on local machine it was called in constructor
        // and this action wiped newly added migration data so we have called it after full synced
        // enable following line on local when you run migration
        // action.config = this.upgradeWorkflowConfigVersion(action.config);

        addDefaultNestedWidgetsIfNeeded(action.config);
        this.initConfig(action.config);
        this.workflowConfigFullUpdateLoaded();
        this.alertHandlerService.showAlertFn('success', 'Synced full session');
      }
    });

    this.initPageTemplateListening();
  }

  private initPageTemplateListening() {
    if (notInJestTest()) {
      // Handle template editor route configuration
      effect(() => {
        if (this.routeService?.isTemplateEditorRoute()) {
          this.workflowConfig$?.next(templateEditorWorkflowConfig);
          this.updateTemplateEditorConfig();
        }
      });

      // Handle template editor page template configuration
      effect(() => {
        if (this.routeService?.isTemplateEditorRoute()) {
          this.updateTemplateEditorConfig();
          const pageTemplate = this.pageTemplateService.currentPageTemplate()?.devicesPageConfig;
          if (pageTemplate) {
            this.updateWorkflowWithPageTemplate(pageTemplate);
            this.systemConfigService.updateConfig(
              () => mapWorkflowToSystem(templateEditorWorkflowConfig),
              false,
            );
          }
        }
      });
    }

    // Subscribe to page template changes
    this.systemConfigService.getPageTemplateConfig().subscribe((pageTemplate) => {
      if (pageTemplate) {
        this.updateWorkflowWithPageTemplate(pageTemplate);
      }
    });
  }

  public exportDiffHistory() {
    exportDiffHistory(this.sentHistoryStack, this.receivedHistoryStack, this.workflowConfig$.value);
  }

  private updateTemplateEditorConfig() {
    this.activeConfigService?.updateConfig((config) => ({
      ...config,
      activeDevice: config?.activeDevice || 'mobile',
      activePageIndex: 0,
      activeRole: 'template',
      activeRoleIndex: 0,
      activeCanvasSubscription: 'default',
      activeZone: 'template',
    }));
  }

  private updateWorkflowWithPageTemplate(pageTemplate: PageTemplateConfig) {
    const devicesConfig = templateEditorWorkflowConfig.permissionSections.roles[0].zones['template']?.canvases
      .subscription['default'] as WorkflowZoneDevicesConfig;

    if (!devicesConfig) return;

    const deviceTypes: Array<keyof WorkflowZoneDevicesConfig> = ['mobile', 'tablet', 'desktop'];

    deviceTypes.forEach((device) => {
      devicesConfig[device] = {
        zoneTypeConfig: 'new',
        zoneConfiguration: {} as ZoneConfiguration,
        pages: [pageTemplate[device]],
      };
    });

    this.workflowConfig$.next(templateEditorWorkflowConfig);
  }

  public applyDiff(diff: jsondiffpatch.Delta) {
    this.updateConfig(
      (oldConfig) => {
        this.redoHistoryStack.pop();
        this.undoHistoryStack.push(diff);

        return this.jsondiffpatchInstance.patch(oldConfig, diff) as WorkflowConfig;
      },
      false,
      // consider a redo operation to handle history correctly
      true,
      true,
      false,
    );
  }

  /*
    Upgrades workflow config from one version to next if needed
  */
  private upgradeWorkflowConfigVersion(workflowConfig: WorkflowConfig): WorkflowConfig {
    // update the workflowConfigLatestVersion const everytime the workflow config version is updated
    const latestVersion = workflowConfigLatestVersion;
    const firstAvailableVersion = getFirstMigrationVersion(workflowConfig);
    if (!workflowConfig?.version) {
      workflowConfig.version = firstAvailableVersion;
      Sentry.addBreadcrumb({
        category: 'Workflow Migration',
        message: 'Missing Version from Workflow Config - first available version applied',
        level: 'warning',
        data: {
          projectId: workflowConfig?.projectId,
        },
      });
    }

    if (workflowConfig?.version !== latestVersion) {
      const upgradeMappings = getMigrationFunctions(workflowConfig);
      // should continually upgrade until up to latest version
      while (
        workflowConfig?.version !== latestVersion &&
        upgradeMappings.get(workflowConfig?.version ?? latestVersion)
      ) {
        workflowConfig = objectMapper.merge(
          workflowConfig,
          upgradeMappings.get(workflowConfig?.version ?? latestVersion)?.() ?? {},
        ) as WorkflowConfig;
      }
    }

    if (workflowConfig.permissionSections) {
      workflowConfig.permissionSections.projectForms = rebuildProjectForms(workflowConfig);
    }
    return workflowConfig;
  }

  storeConfig(config: WorkflowConfig): Promise<void> {
    this.workflowConfig$.next(config);
    this.worker.postMessage(config);
    return Promise.resolve();
  }

  getConfig(): Observable<WorkflowConfig> {
    return this.workflowConfig$;
  }

  getLatestConfig(): WorkflowConfig {
    /* This method removes the need for subscribing.
    You can just call it directly to retrieve the WorkflowConfig. */
    return this.workflowConfig$.getValue();
  }

  undo() {
    this.updateConfig(
      (oldConfig) => {
        const delta = this.undoHistoryStack.pop();
        const reverseDelta = this.jsondiffpatchInstance.reverse(delta);

        this.redoHistoryStack.push(delta);

        return this.jsondiffpatchInstance.patch(oldConfig, reverseDelta) as WorkflowConfig;
      },
      false,
      false,
      true,
    );
  }

  redo() {
    this.updateConfig(
      (oldConfig) => {
        const delta = this.redoHistoryStack.pop();
        this.undoHistoryStack.push(delta);

        return this.jsondiffpatchInstance.patch(oldConfig, delta) as WorkflowConfig;
      },
      false,
      true,
      true,
    );
  }

  private recordDiffAndUpdate(config: WorkflowConfig): jsondiffpatch.Delta {
    const delta = this.jsondiffpatchInstance.diff(this.finalWorkflowConfig, config);
    this.finalWorkflowConfig = JSON.parse(JSON.stringify(config));
    return delta;
  }

  initConfig(config: WorkflowConfig): Promise<void> {
    const originalConfig = cloneDeep(config);
    let after = this.upgradeWorkflowConfigVersion(config);
    after = this.pageNavigationMapService.createPageNavigationMap(after);
    const delta = this.jsondiffpatchInstance.diff(originalConfig, after);

    this.systemConfigService.updateConfig(() => {
      return mapWorkflowToSystem(after);
    }, false);
    this.finalWorkflowConfig = cloneDeep(after); // reset to the current workflow config on init (deep clone to avoid mutations with after)

    if (delta) {
      const projectId = this.store.selectSnapshot(ProjectsState.getActiveProject).id;
      this.lastChangeSentId = new UUID().toString();
      const stack = `Captured trace (not an actual error):\n`;
      const time = new Date();
      this.sentHistoryStack.push({
        diff: delta,
        stack: stack,
        time: time,
        id: this.lastChangeSentId,
      });
      this.socketService.distributeWorkflowConfigChange(projectId, delta, this.lastChangeSentId, stack, time);
    }
    return this.storeConfig(after);
  }

  updateConfig(
    fn: (config: WorkflowConfig) => WorkflowConfig,
    isGridPositionUpdate = false,
    isRedoOperation = false,
    undoOrRedoOperation = false,
    shouldDistributeChange = true,
  ) {
    const stack = new Error().stack ?? '';
    this.configUpdatesLoopDetector(fn, stack);

    // Add update to queue
    this.updateQueue.push({
      fn,
      isGridPositionUpdate,
      isRedoOperation,
      undoOrRedoOperation,
      shouldDistributeChange,
      stackTrace: stack,
    });

    // Clear existing timeout
    if (this.queueTimeout) {
      clearTimeout(this.queueTimeout);
    }

    // Set new timeout to process queue
    this.queueTimeout = setTimeout(() => {
      this.processUpdateQueue();
    }, this.BATCH_DELAY);
  }

  private configUpdatesLoopDetector(fn: (config: WorkflowConfig) => WorkflowConfig, stack: string) {
    const hash = this.generateCallHash(fn, stack);

    const now = Date.now();
    this.recentUpdateConfigCalls = this.recentUpdateConfigCalls.filter(
      (call) => now - call.timestamp < this.LOOP_DETECTION_WINDOW,
    );
    this.recentUpdateConfigCalls.push({ hash, timestamp: now });

    const duplicateCalls = this.recentUpdateConfigCalls.filter((call) => call.hash === hash).length;
    if (duplicateCalls >= this.MAX_DUPLICATE_CALLS) {
      console.error('[Loop Detection] updateConfig has been called repeatedly with the same context.');
      console.error(stack);
    }
  }

  private generateCallHash(fn: (config: WorkflowConfig) => WorkflowConfig, stack: string): string {
    return `${fn.toString().substring(0, 100)}::${stack.split('\n')[2] ?? ''}`;
  }

  private processUpdateQueue() {
    if (this.updateQueue.length === 0) return;
    this.getConfig()
      .pipe(
        take(1),
        tap((initialConfig) => {
          window.requestIdleCallback(() => {
            // Process all queued updates in sequence

            const finalConfig = this.updateQueue.reduce((config, update) => {
              const after = update.fn(this.upgradeWorkflowConfigVersion(config));

              if (after.permissionSections) {
                after.permissionSections.projectForms = rebuildProjectForms(after);
              }

              // Record diff and update history if needed
              const delta = this.recordDiffAndUpdate(after);

              if (!update.undoOrRedoOperation && delta) {
                this.undoHistoryStack.push(delta);
                if (!update.isRedoOperation) {
                  this.redoHistoryStack = new Stack();
                }
              }

              // Handle distribution of changes
              if (update.shouldDistributeChange && delta && !this.routeService.isTemplateEditorRoute()) {
                this.store.dispatch(
                  new AddProgressBarWorkflowStack({ uniqueId: 'updateConfigDistributeChange' }),
                );
                const projectId = this.store.selectSnapshot(ProjectsState.getActiveProject).id;
                this.lastChangeSentId = new UUID().toString();
                const stack = `Captured trace (not an actual error):\n${update.stackTrace}`;
                const time = new Date();
                this.sentHistoryStack.push({
                  diff: delta,
                  stack: stack,
                  time: time,
                  id: this.lastChangeSentId,
                });
                this.socketService.distributeWorkflowConfigChange(
                  projectId,
                  delta,
                  this.lastChangeSentId,
                  stack,
                  time,
                );
              }
              return after;
            }, initialConfig);

            window.requestIdleCallback(() => this.storeConfig(finalConfig));
            this.systemConfigService.updateConfig(() => {
              return mapWorkflowToSystem(finalConfig);
            }, false);
            window.requestIdleCallback(() => {
              // Only emit if no grid position updates in the queue
              const hasGridUpdates = this.updateQueue.some((update) => update.isGridPositionUpdate);
              // Only emit the config if not a grid position update
              // gridster will update and config updates but want to avoid a rerender of the canvas
              if (!hasGridUpdates) this.workflowConfig$.next(finalConfig);
            });
            // Clear the queue
            this.updateQueue = [];
          });
        }),
      )
      .subscribe();
  }

  private getWorkFlowDataFromLocalStorage(): WorkflowConfig {
    if (
      localStorage.getItem('workflowConfig') !== null &&
      localStorage.getItem('workflowConfig') !== 'undefined'
    ) {
      return JSON.parse(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        LZString.decompress(localStorage.getItem('workflowConfig')!),
      ) as WorkflowConfig;
    } else {
      return defaultWorkflowConfig;
    }
  }

  public clearAllDiffHistoryStacks() {
    this.undoHistoryStack.clear();
    this.redoHistoryStack.clear();
    this.sentHistoryStack.clear();
    this.receivedHistoryStack.clear();
  }
}

function notInJestTest() {
  try {
    return process?.env?.['JEST_WORKER_ID'] === undefined;
  } catch (e) {
    return true;
  }
}

/**
 * Export function that works with your existing Stack implementation
 */
async function exportDiffHistory(
  sentHistoryStack: Stack<{ diff: any; time: Date; stack: string | undefined; status?: string }>,
  receivedHistoryStack: Stack<{ diff: any; time: Date; stack: string | undefined }>,
  workflowConfig: WorkflowConfig,
): Promise<void> {
  try {
    // Since your Stack implementation has private storage,
    // we need to get all items by popping and then pushing them back
    const sentItems: Array<{ diff: any; time: Date; stack: string | undefined; status?: string }> = [];
    const receivedItems: Array<{ diff: any; time: Date; stack: string | undefined }> = [];

    // Temporarily store and retrieve sent items
    const sentStackSize = sentHistoryStack.size();
    for (let i = 0; i < sentStackSize; i++) {
      const item = sentHistoryStack.pop();
      if (item) sentItems.unshift(item); // Add to front to maintain order
    }

    // Push items back to maintain the stack
    sentItems.forEach((item) => sentHistoryStack.push(item));

    // Do the same for received items
    const receivedStackSize = receivedHistoryStack.size();
    for (let i = 0; i < receivedStackSize; i++) {
      const item = receivedHistoryStack.pop();
      if (item) receivedItems.unshift(item); // Add to front to maintain order
    }

    // Push items back to maintain the stack
    receivedItems.forEach((item) => receivedHistoryStack.push(item));

    // Sort arrays by time
    sentItems.sort((a, b) => a.time.getTime() - b.time.getTime());
    receivedItems.sort((a, b) => a.time.getTime() - b.time.getTime());

    // Format the data for export
    const sentData = sentItems.map((item, index) => ({
      id: index,
      timestamp: item.time.toISOString(),
      diff: item.diff,
      stackTrace: item.stack,
      diffStatus: item.status ? item.status : WorkflowConfigChangeStatus.SUCCESS,
    }));

    const receivedData = receivedItems.map((item, index) => ({
      id: index,
      timestamp: item.time.toISOString(),
      diff: item.diff,
      stackTrace: item.stack,
    }));

    // Create JSON strings
    const sentJson = JSON.stringify(sentData, null, 2);
    const receivedJson = JSON.stringify(receivedData, null, 2);
    const workflowConfigJson = JSON.stringify(workflowConfig, null, 2);

    // Create zip file
    const zip = new JSZip();

    // Add files to zip
    zip.file('sent_diffs.json', sentJson);
    zip.file('received_diffs.json', receivedJson);
    zip.file('workflow_config.json', workflowConfigJson);

    // Generate zip file
    const zipBlob = await zip.generateAsync({ type: 'blob' });

    // Download the zip file
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    saveAs(zipBlob, `diff-history-${timestamp}.zip`);

    console.log('Diff history exported successfully');
  } catch (error) {
    console.error('Error exporting diff history:', error);
    throw error;
  }
}
